import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';

// --- Type Definitions ---

interface Vendor {
  vendorIdx: number;
  name: string;
  category: string; // API 응답: category.name
  categoryIdx: number;
  contact: string;
  email: string;
  vendorType?: string;
  canInvite?: boolean;
  status: 'ACTIVE' | 'INACTIVE'; // 활성, 비활성(대기)
  createAt: string; // 초대일
  lastLogin?: string; // 마지막 로그인 (API에 있다면)
}

interface VendorStats {
  totalCount: number;
  activeCount: number;
  pendingCount: number; // INACTIVE 상태
  newCount: number;     // 이번달 신규
}

interface CategoryItem {
  vendorCategoryIdx: number;
  name: string;
  depth?: number;
  orderNo?: number;
  children?: CategoryItem[];
  parentName?: string;
}

type InvitePrecheckMode = 'UPGRADE_LOCAL' | 'LINK_EXISTING' | 'ALREADY_PARTNERED' | 'REVIEW_REQUIRED';
type InviteActionMode = 'UPGRADE_LOCAL' | 'LINK_EXISTING';

interface InvitePrecheckResponse {
  mode: InvitePrecheckMode;
  message?: string;
  confirmToken?: string;
  expiresInSeconds?: number;
  inviteRequestIdx?: number;
}

interface InviteConfirmResponse {
  mode: InviteActionMode;
  message?: string;
  partnerVendorIdx?: number;
  rollbackToken?: string;
  rollbackExpiresInSeconds?: number;
  issuedLoginId?: string;
  issuedTempPassword?: string;
}

interface InviteRollbackInfo {
  rollbackToken: string;
  mode: InviteActionMode;
  message: string;
  rollbackExpiresInSeconds: number;
  partnerVendorIdx?: number;
  inviteRequestIdx?: number;
}

const onlyDigits = (value: string) => value.replace(/\D/g, '').slice(0, 11);
const formatMobile = (value: string) => {
  const digits = onlyDigits(value);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
};
const isValidKoreanMobile010 = (value: string) => /^010-\d{4}-\d{4}$/.test(value);
const getInviteModeLabel = (mode: InviteActionMode) =>
  mode === 'UPGRADE_LOCAL' ? '신규 협력업체 등록' : '기존 업체 연결';
const getPrecheckGuideMessage = (mode: InviteActionMode) =>
  mode === 'UPGRADE_LOCAL'
    ? '기존 가입 이력이 확인되지 않아 신규 협력업체로 등록됩니다.'
    : '기존 가입 업체를 우리 업체의 협력 파트너로 연결합니다.';
const sanitizeInviteMessage = (message?: string) => {
  if (!message) return '';
  return message
    .replace(/confirm 호출 시/gi, '초대 확정 시')
    .replace(/local 업체/gi, '미가입 협력업체')
    .replace(/로컬 업체/gi, '미가입 협력업체')
    .replace(/guest로 승격됩니다/gi, '협력 파트너로 등록됩니다')
    .replace(/guest/gi, '협력 파트너');
};

export default function VendorInvite() {
  // --- State ---
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [stats, setStats] = useState<VendorStats>({
    totalCount: 0,
    activeCount: 0,
    pendingCount: 0,
    newCount: 0
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState<CategoryItem[]>([]); // 카테고리 목록

  // Modals
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false); // 상태 수정용 모달
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [isInvitingVendor, setIsInvitingVendor] = useState(false);
  const [isRollingBackInvite, setIsRollingBackInvite] = useState(false);
  const [inviteRollbackInfo, setInviteRollbackInfo] = useState<InviteRollbackInfo | null>(null);
  const rollbackAvailableMinutes = inviteRollbackInfo
    ? Math.ceil(inviteRollbackInfo.rollbackExpiresInSeconds / 60)
    : 0;
  const isRollbackTargetVendor = (vendorIdx: number) => {
    if (!inviteRollbackInfo?.rollbackToken || inviteRollbackInfo.partnerVendorIdx == null) return false;
    return vendorIdx === inviteRollbackInfo.partnerVendorIdx;
  };
  const isRollbackTargetVisible = inviteRollbackInfo
    ? vendors.some((vendor) => isRollbackTargetVendor(vendor.vendorIdx))
    : false;

  // --- API Calls ---

  // 0. 카테고리 목록 조회 (초대 모달용)
  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/vendor/category', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('카테고리 조회 실패');

      const payload = await response.json();
      const rawTree: CategoryItem[] = Array.isArray(payload)
        ? payload
        : (payload?.status ? payload.data : []);

      const sortedTree = [...rawTree].sort(
        (a, b) => (a.orderNo ?? Number.MAX_SAFE_INTEGER) - (b.orderNo ?? Number.MAX_SAFE_INTEGER)
      );

      const flattened = sortedTree.flatMap((parent) => {
        const parentNode: CategoryItem = {
          vendorCategoryIdx: parent.vendorCategoryIdx,
          name: parent.name,
          depth: parent.depth,
          orderNo: parent.orderNo
        };

        const children = (parent.children || [])
          .slice()
          .sort((a, b) => (a.orderNo ?? Number.MAX_SAFE_INTEGER) - (b.orderNo ?? Number.MAX_SAFE_INTEGER))
          .map((child) => ({
            vendorCategoryIdx: child.vendorCategoryIdx,
            name: child.name,
            depth: child.depth,
            orderNo: child.orderNo,
            parentName: parent.name
          }));

        return [parentNode, ...children];
      });

      setCategories(flattened);
    } catch (error) {
      console.error('Failed to fetch categories', error);
    }
  }, []);

  // 1. 업체 목록 조회 (GET /vendor/invited)
  const fetchVendors = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('size', '50');

      const response = await fetch(`https://api.wedmin.biz/vendor/invited?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();
      if (result.status && result.data) {
        const vendors = result.data.vendors || result.data.items || [];
        const stats = result.data.stats as VendorStats | undefined;
        
        // 데이터 매핑
        const mappedVendors: Vendor[] = vendors.map((v: any) => ({
          vendorIdx: v.vendorIdx,
          name: v.name,
          category: v.category?.name || '미지정',
          categoryIdx: v.category?.vendorCategoryIdx || 0,
          contact: v.contact || '-',
          email: v.email || '',
          vendorType: v.vendorType,
          canInvite: typeof v.canInvite === 'boolean' ? v.canInvite : true,
          status: v.status,
          createAt: new Date(v.createdDt || v.createDt || Date.now()).toLocaleDateString(),
          lastLogin: v.users?.[0]?.lastLoginDt ? new Date(v.users[0].lastLoginDt).toLocaleString() : '-'
        }));

        setVendors(mappedVendors);
        if (stats) {
          setStats(stats);
        } else {
          const totalCount = mappedVendors.length;
          const activeCount = mappedVendors.filter((v) => v.status === 'ACTIVE').length;
          const pendingCount = mappedVendors.filter((v) => v.status === 'INACTIVE').length;
          setStats({ totalCount, activeCount, pendingCount, newCount: 0 });
        }
      }
    } catch (error) {
      console.error('Fetch vendors error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchVendors();
  }, [fetchCategories, fetchVendors]);

  // 2. 업체 초대 (POST /vendor/invite/precheck -> POST /vendor/invite/confirm)
  const handleInviteVendor = async (formData: any) => {
    if (isInvitingVendor) return;

    const vendorIdx = Number(formData.vendorIdx);
    if (!Number.isInteger(vendorIdx) || vendorIdx <= 0) {
      alert('유효한 업체 ID(vendorIdx)를 입력해주세요.');
      return;
    }

    const loginId = String(formData.loginId || '').trim();
    if (!loginId) {
      alert('로그인 ID를 입력해주세요.');
      return;
    }

    setIsInvitingVendor(true);
    try {
      const token = localStorage.getItem('accessToken');
      const formattedContact = formData.contact ? formatMobile(String(formData.contact)) : '';
      const precheckPayload = {
        vendorIdx,
        loginId,
        ...(formattedContact ? { contact: formattedContact } : {}),
        ...(typeof formData.managerName === 'string' && formData.managerName.trim()
          ? { managerName: formData.managerName.trim() }
          : {}),
        ...(typeof formData.email === 'string' && formData.email.trim()
          ? { email: formData.email.trim() }
          : {})
      };

      const precheckResponse = await fetch('https://api.wedmin.biz/vendor/invite/precheck', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(precheckPayload)
      });

      const precheckResult = await precheckResponse.json().catch(() => null);
      const precheckData: InvitePrecheckResponse | null =
        precheckResult?.status && precheckResult?.data ? precheckResult.data : precheckResult;

      if (!precheckResponse.ok || !precheckData?.mode) {
        alert(`초대 사전검증 실패: ${precheckResult?.message || precheckData?.message || '오류가 발생했습니다.'}`);
        return;
      }

      if (precheckData.mode === 'ALREADY_PARTNERED') {
        alert(sanitizeInviteMessage(precheckData.message) || '이미 파트너로 연결된 업체입니다.');
        return;
      }

      if (precheckData.mode === 'REVIEW_REQUIRED') {
        alert(
          `${sanitizeInviteMessage(precheckData.message) || '자동 진행이 어려운 상태입니다.'}\n연락처를 다시 확인한 후 다시 시도해주세요.`
        );
        return;
      }

      const confirmableMode = precheckData.mode === 'LINK_EXISTING' || precheckData.mode === 'UPGRADE_LOCAL';
      if (!confirmableMode) {
        alert(sanitizeInviteMessage(precheckData.message) || '확정 가능한 초대 모드가 아닙니다.');
        return;
      }

      if (!precheckData.confirmToken) {
        alert('확정 토큰을 받지 못했습니다. 다시 시도해주세요.');
        return;
      }

      const modeLabel = getInviteModeLabel(precheckData.mode);
      const precheckMessage = sanitizeInviteMessage(precheckData.message) || getPrecheckGuideMessage(precheckData.mode);
      const shouldConfirm = window.confirm(
        `[${modeLabel}]\n${precheckMessage}\n\n이대로 진행할까요?`
      );
      if (!shouldConfirm) return;

      const confirmResponse = await fetch('https://api.wedmin.biz/vendor/invite/confirm', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ confirmToken: precheckData.confirmToken })
      });

      const confirmResult = await confirmResponse.json().catch(() => null);
      const confirmData: InviteConfirmResponse | null =
        confirmResult?.status && confirmResult?.data ? confirmResult.data : confirmResult;

      if (!confirmResponse.ok || !confirmData?.mode) {
        alert(`초대 확정 실패: ${confirmResult?.message || confirmData?.message || '오류가 발생했습니다.'}`);
        return;
      }

      if (confirmData.rollbackToken) {
        setInviteRollbackInfo({
          rollbackToken: confirmData.rollbackToken,
          mode: confirmData.mode,
          message: sanitizeInviteMessage(confirmData.message) || '초대/연결이 완료되었습니다.',
          rollbackExpiresInSeconds: confirmData.rollbackExpiresInSeconds || 1800,
          partnerVendorIdx: confirmData.partnerVendorIdx,
          inviteRequestIdx: precheckData.inviteRequestIdx
        });
      } else {
        setInviteRollbackInfo(null);
      }

      const successMessage = sanitizeInviteMessage(confirmData.message) || '초대/연결이 완료되었습니다.';
      const credentialMessage = confirmData.issuedLoginId || confirmData.issuedTempPassword
        ? `\n\n로그인 ID: ${confirmData.issuedLoginId || '-'}\n임시 비밀번호: ${confirmData.issuedTempPassword || '-'}`
        : '';
      alert(`${successMessage}${credentialMessage}`);
      setShowInviteModal(false);
      fetchVendors(); // 목록 갱신
    } catch (error) {
      console.error('Invite error:', error);
      alert('오류가 발생했습니다.');
    } finally {
      setIsInvitingVendor(false);
    }
  };

  const handleRollbackInvite = async () => {
    if (!inviteRollbackInfo?.rollbackToken || isRollingBackInvite) return;
    const shouldRollback = window.confirm('방금 보낸 초대를 취소하시겠습니까?');
    if (!shouldRollback) return;

    try {
      setIsRollingBackInvite(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/vendor/invite/cancel', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rollbackToken: inviteRollbackInfo.rollbackToken })
      });

      const result = await response.json().catch(() => null);
      const message = sanitizeInviteMessage(result?.data?.message || result?.message) || '초대 취소가 완료되었습니다.';
      const actionType: string | undefined = result?.data?.actionType || result?.actionType;
      const actionTypeLabel =
        actionType === 'UPGRADE_LOCAL'
          ? getInviteModeLabel('UPGRADE_LOCAL')
          : actionType === 'LINK_EXISTING'
            ? getInviteModeLabel('LINK_EXISTING')
            : '';
      if (!response.ok || result?.status === false) {
        alert(`롤백 실패: ${result?.message || '오류가 발생했습니다.'}`);
        return;
      }

      alert(actionTypeLabel ? `${message}\n처리 유형: ${actionTypeLabel}` : message);
      setInviteRollbackInfo(null);
      fetchVendors();
    } catch (error) {
      console.error('Rollback invite error:', error);
      alert('롤백 처리 중 오류가 발생했습니다.');
    } finally {
      setIsRollingBackInvite(false);
    }
  };

  // 3. 업체 상태 수정 (PATCH /vendor/{id})
  const handleUpdateStatus = async (vendorIdx: number, newStatus: string) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/vendor/${vendorIdx}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus }) // 상태만 수정
      });

      const result = await response.json();
      if (response.ok) {
        alert('상태가 변경되었습니다.');
        setShowEditModal(false);
        fetchVendors();
      } else {
        alert(`수정 실패: ${result.message}`);
      }
    } catch (error) {
      console.error('Update error:', error);
    }
  };

  // 4. 업체 삭제 (DELETE /vendor/{id})
  const handleDeleteVendor = async (vendorIdx: number) => {
    if (!confirm('해당 업체와의 연결을 해제하시겠습니까? 업체 본체 정보는 삭제되지 않습니다.')) return;

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/vendor/${vendorIdx}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('연결 해제가 완료되었습니다.');
        fetchVendors();
      } else {
        const result = await response.json();
        alert(`연결 해제 실패: ${result.message}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  // --- UI Helpers ---
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800';
      case 'INACTIVE': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    return status === 'ACTIVE' ? '활성' : '비활성(대기)';
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 p-8 overflow-y-auto">
        {/* 헤더 */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">업체 초대 관리</h1>
            <p className="text-gray-600">협력 업체를 초대하고 상태를 관리합니다</p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
          >
            <i className="ri-user-add-line"></i>
            업체 초대
          </button>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">총 업체수</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalCount}개</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-store-line text-blue-600 text-xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">활성 업체</p>
                <p className="text-2xl font-bold text-green-600">{stats.activeCount}개</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-check-double-line text-green-600 text-xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">비활성/대기</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pendingCount || 0}개</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <i className="ri-time-line text-yellow-600 text-xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">이번 달 신규</p>
                <p className="text-2xl font-bold text-blue-600">{stats.newCount || 0}개</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-add-circle-line text-blue-600 text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* 업체 목록 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">연결 업체 목록</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">업체명</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">카테고리</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">연락처</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">이메일</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">상태</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">초대일</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">마지막 로그인</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">관리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8">로딩 중...</td>
                  </tr>
                ) : vendors.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">초대된 업체가 없습니다.</td>
                  </tr>
                ) : (
                  vendors.map((vendor) => (
                    <tr key={vendor.vendorIdx} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <i className="ri-store-line text-blue-600"></i>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{vendor.name}</div>
                            <div className="text-sm text-gray-500">ID: {vendor.vendorIdx}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-700">
                          {vendor.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{vendor.contact}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{vendor.email || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(vendor.status)}`}>
                          {getStatusLabel(vendor.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{vendor.createAt}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{vendor.lastLogin}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          {isRollbackTargetVendor(vendor.vendorIdx) && (
                            <button
                              onClick={handleRollbackInvite}
                              disabled={isRollingBackInvite}
                              className={`px-2 py-1 text-xs rounded border ${
                                isRollingBackInvite
                                  ? 'text-amber-400 border-amber-200 bg-amber-50 cursor-not-allowed'
                                  : 'text-amber-700 border-amber-300 hover:bg-amber-50'
                              }`}
                            >
                              {isRollingBackInvite ? '취소 중...' : '초대 취소'}
                            </button>
                          )}
                          <button 
                            onClick={() => {
                              setSelectedVendor(vendor);
                              setShowEditModal(true);
                            }}
                            className="text-green-600 hover:text-green-800 w-8 h-8 flex items-center justify-center cursor-pointer"
                            title="상태 수정"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button 
                            onClick={() => handleDeleteVendor(vendor.vendorIdx)}
                            className="text-red-600 hover:text-red-800 w-8 h-8 flex items-center justify-center cursor-pointer"
                            title="연결 해제"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {inviteRollbackInfo && (
            <div className="flex flex-wrap items-center gap-3 px-6 py-3">
              <p className="text-xs text-amber-700">
                초대 취소는 방금 초대한 업체에서만 가능하며, 약 {rollbackAvailableMinutes}분 뒤 자동으로 만료됩니다.
              </p>
              {!isRollbackTargetVisible && (
                <button
                  type="button"
                  onClick={handleRollbackInvite}
                  disabled={isRollingBackInvite}
                  className={`px-3 py-1 text-xs rounded border ${
                    isRollingBackInvite
                      ? 'text-amber-400 border-amber-200 bg-amber-50 cursor-not-allowed'
                      : 'text-amber-700 border-amber-300 hover:bg-amber-50'
                  }`}
                >
                  {isRollingBackInvite ? '취소 중...' : '초대 취소'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* --- Modals --- */}

      {/* 1. 업체 초대 모달 */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">업체 초대</h2>
              <button onClick={() => setShowInviteModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                handleInviteVendor(Object.fromEntries(formData));
              }}
              className="p-6 space-y-6"
            >
              {/* 기본 정보 */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">기본 정보</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">업체 ID(vendorIdx) *</label>
                    <input
                      type="number"
                      name="vendorIdx"
                      min={1}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="예: 21"
                    />
                    <p className="mt-1 text-xs text-gray-500">초대할 업체의 ID를 입력해주세요.</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">카테고리 (참고, 선택)</label>
                    <select name="categoryIdx" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">카테고리 선택</option>
                      {categories.map(cat => (
                        <option key={cat.vendorCategoryIdx} value={cat.vendorCategoryIdx}>
                          {cat.parentName ? `${cat.parentName} > ${cat.name}` : cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* 계정 및 연락처 정보 */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">계정 및 연락처 정보</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">로그인 ID *</label>
                    <input
                      type="text"
                      name="loginId"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="예: vendor01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">대표 연락처 (선택)</label>
                    <input
                      type="tel"
                      name="contact"
                      onChange={(e) => {
                        e.currentTarget.value = formatMobile(e.currentTarget.value);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="010-1234-5678"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">담당자 성함 (선택)</label>
                    <input type="text" name="managerName" className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="홍길동" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">이메일 (선택)</label>
                    <input type="email" name="email" className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="vendor@example.com" />
                  </div>
                </div>
              </div>

              {/* 사업자 정보 */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">추가 정보 (선택)</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">사업자등록번호</label>
                    <input type="text" name="businessNumber" className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="123-45-67890" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">사업장 주소</label>
                    <input type="text" name="address" className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="사업장 주소를 입력하세요" />
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 -mx-6 -mb-6">
                <button type="button" onClick={() => setShowInviteModal(false)} className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg">취소</button>
                <button
                  type="submit"
                  disabled={isInvitingVendor}
                  className={`px-6 py-2 rounded-lg text-white ${
                    isInvitingVendor ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {isInvitingVendor ? '처리 중...' : '초대 발송'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2. 상태 수정 모달 (정보는 읽기전용, 상태만 수정) */}
      {showEditModal && selectedVendor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">업체 상태 변경</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* 읽기 전용 정보 */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>업체명:</span>
                  <span className="font-medium text-gray-900">{selectedVendor.name}</span>
                </div>
                <div className="flex justify-between">
                  <span>연락처:</span>
                  <span className="font-medium text-gray-900">{selectedVendor.contact}</span>
                </div>
                <div className="flex justify-between">
                  <span>이메일:</span>
                  <span className="font-medium text-gray-900">{selectedVendor.email}</span>
                </div>
              </div>

              <div className="p-2 text-xs text-red-500 bg-red-50 rounded">
                * 상세 정보 수정은 해당 업체 관리자만 가능합니다.<br/>
                * 여기서는 업체의 활성/비활성 상태만 변경할 수 있습니다.
              </div>

              {/* 상태 변경 폼 */}
              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target as HTMLFormElement);
                  handleUpdateStatus(selectedVendor.vendorIdx, formData.get('status') as string);
                }}
              >
                <label className="block text-sm font-medium text-gray-700 mb-2">계정 상태</label>
                <select 
                  name="status" 
                  defaultValue={selectedVendor.status}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ACTIVE">활성 (정상 이용 가능)</option>
                  <option value="INACTIVE">비활성 (로그인 차단)</option>
                </select>

                <div className="mt-6 flex justify-end gap-3">
                  <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg">취소</button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">변경 저장</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
